import React from "react";
import _ from "lodash";
import { Page, Text, View, Image } from "@react-pdf/renderer";

import {
  ADDRESS_TYPES,
  QUALIFICATION_TYPES
} from "../../../constants/constants";
import CDK_Logo from "../../../images/CDKGlobalWM_BLK.jpg";
import commonStyles from "../PDFStyles/common";
import styles from "../PDFStyles/personalProfile";
import { getAddressDetails } from "../../../util/candidateDataFilters";
import {
  getFormattedDate,
  getFullName,
  getGenderName,
  getMaritalName
} from "../../../util/helpers";
import {
  findTotalExperienceInDays,
  getBloodGroup,
  getFullTimeOrPartTime,
  getNationality,
  getNoOfYearsMonthsDaysString,
  getQualificationDetails,
  getRelationShipName,
  getStateName,
  getTitle
} from "../../../util/onBoardingDataFilters";

const getQualificationView = (
  headerText,
  qualificationName,
  qualificationDetails,
  states
) => {
  return (
    <View style={commonStyles.tableRow}>
      <View style={styles.tableColWidth13}>
        <Text style={styles.tableHeader}>{headerText}</Text>
      </View>
      <View style={styles.tableColWidth16}>
        <Text style={commonStyles.tableCell}>{qualificationName}</Text>
      </View>
      <View style={styles.tableColWidth16}>
        <Text style={commonStyles.tableCell}>
          {qualificationDetails?(qualificationDetails.university || qualificationDetails.degree):""}
        </Text>
      </View>
      <View style={styles.tableColWidth16}>
        <Text style={commonStyles.tableCell}>
          {getStateName(qualificationDetails ? qualificationDetails.state : "", states)}
        </Text>
      </View>
      <View style={styles.tableColWidth13}>
        <Text style={commonStyles.tableCell}>
          {qualificationDetails ? qualificationDetails.completionYear : ""}
        </Text>
      </View>
      <View style={styles.tableColWidth13}>
        <Text style={commonStyles.tableCell}>{qualificationDetails ? qualificationDetails.grade : ""}</Text>
      </View>
      <View style={styles.tableColWidth13}>
        <Text style={commonStyles.tableCell}>
          {getFullTimeOrPartTime(qualificationDetails ? qualificationDetails.isFullTimeQualification : "")}
        </Text>
      </View>
    </View>
  );
};

const PersonalProfile = React.memo(
  ({
    candidateDetails,
    genders,
    maritalStatusList,
    relationShipList,
    states
  }) => {
    const permanentAddress = getAddressDetails(
      ADDRESS_TYPES.PERMANENT,
      candidateDetails.addresses
    );
    const currentAddress = getAddressDetails(
      ADDRESS_TYPES.CURRENT,
      candidateDetails.addresses
    );
    const fullName = getFullName(candidateDetails);
    const isPostGraduation = !_.isEmpty(
      getQualificationDetails(
        QUALIFICATION_TYPES.POSTGRADUATION,
        candidateDetails.qualifications
      )
    );

    return (
      <Page size="A4" style={commonStyles.page}>
        <Image style={commonStyles.cdkLogo} src={CDK_Logo} />
        <View style={commonStyles.header}>
          <Text style={styles.profileHeading}>Employee Personal Profile</Text>
        </View>
        <View style={styles.tableWithMarginTop}>
          <View style={commonStyles.tableRow}>
            <View style={styles.fullWidthTableColumn}>
              <Text style={styles.tableHeader}>Personal Details</Text>
            </View>
          </View>

          <View style={commonStyles.tableRow}>
            <View style={styles.firstNameKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                Title (Mr/Ms):
              </Text>
            </View>
            <View style={styles.titleValue}>
              <Text style={commonStyles.tableCell}>
                {getTitle(candidateDetails.title)}
              </Text>
            </View>
            <View style={styles.firstNameKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                Date of Joining:
              </Text>
            </View>
            <View style={styles.dojValue}>
              <Text style={commonStyles.tableCell}>
                {getFormattedDate(candidateDetails.dateofJoining)}
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.firstNameKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                First Name:
              </Text>
            </View>
            <View style={styles.firstNameValue}>
              <Text style={commonStyles.tableCell}>
                {candidateDetails.firstName}
              </Text>
            </View>
            <View style={styles.firstNameKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                Middle Name:
              </Text>
            </View>
            <View style={styles.firstNameKey}>
              <Text style={commonStyles.tableCell}>
                {candidateDetails.middleName}
              </Text>
            </View>
            <View style={styles.personalMailKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                Last Name:
              </Text>
            </View>
            <View style={styles.lastNameValue}>
              <Text style={commonStyles.tableCell}>
                {candidateDetails.lastName}
              </Text>
            </View>
          </View>

          <View style={commonStyles.tableRow}>
            <View style={styles.dateOfBirthKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                DOB(dd-mm-yy):
              </Text>
            </View>
            <View style={styles.dateOfBirthValue}>
              <Text style={commonStyles.tableCell}>
                {getFormattedDate(candidateDetails.birthDate)}
              </Text>
            </View>
            <View style={styles.genderKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                Gender:
              </Text>
            </View>
            <View style={styles.genderValue}>
              <Text style={commonStyles.tableCell}>
                {getGenderName(candidateDetails.gender, genders)}
              </Text>
            </View>
            <View style={styles.personalMailKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                Business Title:
              </Text>
            </View>
            <View style={styles.businessTitleValue}>
              <Text style={commonStyles.tableCell}>
                {candidateDetails.cDKDesignation}
              </Text>
            </View>
          </View>

          <View style={commonStyles.tableRow}>
            <View style={styles.maritalStatusKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                Marital Status :
              </Text>
            </View>
            <View style={styles.maritalStatusValue}>
              <Text style={commonStyles.tableCell}>
                {getMaritalName(
                  candidateDetails.maritalStatus,
                  maritalStatusList,
                  true
                )}
              </Text>
            </View>
            <View style={styles.spouseNameKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                Spouse Name :
              </Text>
            </View>
            <View style={styles.spouseNameValue}>
              <Text style={commonStyles.tableCell}>
                {candidateDetails.spouseName}
              </Text>
            </View>
            <View style={styles.marriageDateKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                Marriage Date :
              </Text>
            </View>
            <View style={styles.marriageDateValue}>
              <Text style={commonStyles.tableCell}>
                {getFormattedDate(candidateDetails.anniversaryDate)}
              </Text>
            </View>
          </View>

          <View style={commonStyles.tableRow}>
            <View style={styles.panCardKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                PAN card :
              </Text>
            </View>
            <View style={styles.panCardValue}>
              <Text style={commonStyles.tableCell}>
                {candidateDetails.panCardNo}
              </Text>
            </View>
            <View style={styles.aadhaarKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                Aadhar No:
              </Text>
            </View>
            <View style={styles.aadhaarValue}>
              <Text style={commonStyles.tableCell}>
                {candidateDetails.adhaarNumber}
              </Text>
            </View>
            <View style={styles.personalMailKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                Personal Email ID:
              </Text>
            </View>
            <View style={styles.personalMailValue}>
              <Text style={commonStyles.tableCell}>
                {candidateDetails.emailId}
              </Text>
            </View>
          </View>

          <View style={commonStyles.tableRow}>
            <View style={styles.panCardKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                Nationality:
              </Text>
            </View>
            <View style={styles.panCardValue}>
              <Text style={commonStyles.tableCell}>
                {getNationality(candidateDetails.nationality)}
              </Text>
            </View>
            <View style={styles.aadhaarKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                Blood Group :
              </Text>
            </View>
            <View style={styles.aadhaarValue}>
              <Text style={commonStyles.tableCell}>
                {getBloodGroup(candidateDetails.bloodGroup)}
              </Text>
            </View>
            <View style={styles.personalMailKey}>
              <Text
                style={{ ...commonStyles.tableCell, ...commonStyles.fontBold }}
              >
                Languages Known:
              </Text>
            </View>
            <View style={styles.personalMailValue}>
              <Text style={commonStyles.tableCell}>
                {candidateDetails.languagesKnown}
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.fullWidthTableColumn}>
              <Text style={styles.tableHeaderWithoutAutoMargin}>
                Bio:
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.fullWidthTableColumn}>
              <Text style={styles.tableHeader}>
                {candidateDetails.postHeldNumber}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.tableWithMarginTop}>
          <View style={commonStyles.tableRow}>
            <View style={styles.fullWidthTableColumn}>
              <Text style={styles.tableHeader}>
                Home Address (Permanent Address)
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.addressLineKey}>
              <Text style={commonStyles.tableCell}>Address Line 1:</Text>
            </View>
            <View style={styles.addressLineValue}>
              <Text style={commonStyles.tableCell}>
                {permanentAddress.addressLine1}
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.addressLineKey}>
              <Text style={commonStyles.tableCell}>Address Line 2:</Text>
            </View>
            <View style={styles.addressLineValue}>
              <Text style={commonStyles.tableCell}>
                {permanentAddress.addressLine2}
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.addressLineKey}>
              <Text style={commonStyles.tableCell}>Address Line 3:</Text>
            </View>
            <View style={styles.addressLineValue}>
              <Text style={commonStyles.tableCell}>
                {permanentAddress.locality}
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.halfQuarterWidthCol}>
              <Text style={commonStyles.tableCell}>Town/City:</Text>
            </View>
            <View style={styles.halfQuarterWidthCol}>
              <Text style={commonStyles.tableCell}>
                {permanentAddress.city}
              </Text>
            </View>
            <View style={styles.halfQuarterWidthCol}>
              <Text style={commonStyles.tableCell}>State:</Text>
            </View>
            <View style={styles.halfQuarterWidthCol}>
              <Text style={commonStyles.tableCell}>
                {getStateName(permanentAddress.state, states)}
              </Text>
            </View>
            <View style={styles.halfQuarterWidthCol}>
              <Text style={commonStyles.tableCell}>Country:</Text>
            </View>
            <View style={styles.halfQuarterWidthCol}>
              <Text style={commonStyles.tableCell}>India</Text>
            </View>
            <View style={styles.halfQuarterWidthCol}>
              <Text style={commonStyles.tableCell}>Postal Code:</Text>
            </View>
            <View style={styles.halfQuarterWidthCol}>
              <Text style={commonStyles.tableCell}>
                {permanentAddress.pincode}
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.phoneKey}>
              <Text style={commonStyles.tableCell}>
                Home Phone (include prefix):
              </Text>
            </View>
            <View style={styles.phoneValue}>
              <Text style={commonStyles.tableCell}>
                {candidateDetails.contactNumber}
              </Text>
            </View>
            <View style={styles.phoneKey}>
              <Text style={commonStyles.tableCell}>
                Mobile Phone (include prefix):
              </Text>
            </View>
            <View style={styles.phoneValue}>
              <Text style={commonStyles.tableCell}>
                {candidateDetails.alternateContactNo}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.tableWithMarginTop}>
          <View style={commonStyles.tableRow}>
            <View style={styles.fullWidthTableColumn}>
              <Text style={styles.tableHeader}>
                Mailing Address(Current Address)
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.addressLineKey}>
              <Text style={commonStyles.tableCell}>Address Line 1:</Text>
            </View>
            <View style={styles.addressLineValue}>
              <Text style={commonStyles.tableCell}>
                {currentAddress.addressLine1}
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.addressLineKey}>
              <Text style={commonStyles.tableCell}>Address Line 2:</Text>
            </View>
            <View style={styles.addressLineValue}>
              <Text style={commonStyles.tableCell}>
                {currentAddress.addressLine2}
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.addressLineKey}>
              <Text style={commonStyles.tableCell}>Address Line 3:</Text>
            </View>
            <View style={styles.addressLineValue}>
              <Text style={commonStyles.tableCell}>
                {currentAddress.locality}
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.halfQuarterWidthCol}>
              <Text style={commonStyles.tableCell}>Town/City:</Text>
            </View>
            <View style={styles.halfQuarterWidthCol}>
              <Text style={commonStyles.tableCell}>{currentAddress.city}</Text>
            </View>
            <View style={styles.halfQuarterWidthCol}>
              <Text style={commonStyles.tableCell}>State:</Text>
            </View>
            <View style={styles.halfQuarterWidthCol}>
              <Text style={commonStyles.tableCell}>
                {getStateName(currentAddress.state, states)}
              </Text>
            </View>
            <View style={styles.halfQuarterWidthCol}>
              <Text style={commonStyles.tableCell}>Country:</Text>
            </View>
            <View style={styles.halfQuarterWidthCol}>
              <Text style={commonStyles.tableCell}>India</Text>
            </View>
            <View style={styles.halfQuarterWidthCol}>
              <Text style={commonStyles.tableCell}>Postal Code:</Text>
            </View>
            <View style={styles.halfQuarterWidthCol}>
              <Text style={commonStyles.tableCell}>
                {currentAddress.pincode}
              </Text>
            </View>
          </View>
        </View>

        <Image break style={commonStyles.cdkLogo} src={CDK_Logo} />

        <View style={styles.tableWithMarginTop}>
          <View style={commonStyles.tableRow}>
            <View style={styles.fullWidthTableColumn}>
              <Text style={styles.tableHeader}>Emergency Contact Details</Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.contactNameKey}>
              <Text style={commonStyles.tableCell}>Contact Name:</Text>
            </View>
            <View style={styles.contactNameValue}>
              <Text style={commonStyles.tableCell}>
                {candidateDetails.emergencyPersonName}
              </Text>
            </View>
            <View style={styles.employeeRelationKey}>
              <Text style={commonStyles.tableCell}>
                Relationship to Employee:
              </Text>
            </View>
            <View style={styles.employeeRelationValue}>
              <Text style={commonStyles.tableCell}>
                {candidateDetails.relationWithMe}
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.phoneKey}>
              <Text style={commonStyles.tableCell}>
                Mobile Phone (include prefix):
              </Text>
            </View>
            <View style={styles.phoneValue}>
              <Text style={commonStyles.tableCell}>
                {candidateDetails.contactNumber}
              </Text>
            </View>
            <View style={styles.phoneKey}>
              <Text style={commonStyles.tableCell}>
                Home Phone (include prefix):
              </Text>
            </View>
            <View style={styles.phoneValue}>
              <Text style={commonStyles.tableCell}>
                {candidateDetails.emergencyHomeNumber}
              </Text>
            </View>
          </View>
        </View>
        <View style={{ ...commonStyles.table, ...commonStyles.marginTop20 }}>
          <View style={commonStyles.tableRow}>
            <View style={commonStyles.tableCol}></View>

            <View style={styles.familyBackgroundDetails}>
              <View style={commonStyles.tableRow}>
                <Text style={styles.tableHeader}>
                  Family Background Details
                </Text>
              </View>
              <View style={commonStyles.hrLine} />
              <View style={commonStyles.flexDirectionRow}>
                <Text
                  style={styles.familyMemberHeaderName}
                >
                  Name
                </Text>
                <View style={commonStyles.borderRight} />
                <Text
                  style={styles.familyMemberHeaderOccupation}
                >
                  Occupation
                </Text>
                <View style={commonStyles.borderRight} />
                <Text style={styles.tableHeader}>DOB (DD/MM/YYYY)</Text>
              </View>
            </View>
          </View>

          {candidateDetails.familyMembers?candidateDetails.familyMembers.map(memberDetails => (
            <View style={commonStyles.tableRow}>
              <View style={styles.contactNameKey}>
                <Text style={styles.tableHeader}>
                  {getRelationShipName(
                    memberDetails.relationShipType,
                    relationShipList
                  )}
                </Text>
              </View>
              <View style={styles.contactNameValue}>
                <Text style={commonStyles.tableCell}>
                  {memberDetails.fullName}
                </Text>
              </View>
              <View style={styles.employeeRelationValue}>
                <Text style={commonStyles.tableCell}>
                  {memberDetails.occupation}
                </Text>
              </View>
              <View style={styles.employeeRelationValue}>
                <Text style={commonStyles.tableCell}>
                  {getFormattedDate(memberDetails.dateOfBirth)}
                </Text>
              </View>
            </View>
          )):null}
        </View>
        <View style={{ ...commonStyles.table, ...commonStyles.marginTop20 }}>
          <View style={commonStyles.tableRow}>
            <View
              style={{ ...commonStyles.tableCol, ...commonStyles.width100 }}
            >
              <Text style={styles.tableHeader}>Educational Details</Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.tableColWidth13}>
              <Text style={styles.tableHeader}>Level</Text>
            </View>
            <View style={styles.tableColWidth16}>
              <Text style={styles.tableHeader}>Qualification</Text>
            </View>
            <View style={styles.tableColWidth16}>
              <Text style={styles.tableHeader}>University / Board</Text>
            </View>
            <View style={styles.tableColWidth16}>
              <Text style={styles.tableHeader}>State</Text>
            </View>
            <View style={styles.tableColWidth13}>
              <Text style={styles.tableHeader}>Year of Passing</Text>
            </View>
            <View style={styles.tableColWidth13}>
              <Text style={styles.tableHeader}>% or CGPA</Text>
            </View>
            <View style={styles.tableColWidth13}>
              <Text style={styles.tableHeader}>Full Time / Part Time</Text>
            </View>
          </View>

          {getQualificationView(
            "CLASSX",
            "CLASSX",
            getQualificationDetails(
              QUALIFICATION_TYPES.CLASSX,
              candidateDetails.qualifications
            ),
            states
          )}

          {getQualificationView(
            "XII Std/Equiv",
            "CLASSXII",
            getQualificationDetails(
              QUALIFICATION_TYPES.CLASSXII,
              candidateDetails.qualifications
            ),
            states
          )}

          {getQualificationView(
            "Graduate",
            getQualificationDetails(
              QUALIFICATION_TYPES.GRADUATION,
              candidateDetails.qualifications
            )? getQualificationDetails(
              QUALIFICATION_TYPES.GRADUATION,
              candidateDetails.qualifications
            ).degree:"",
            getQualificationDetails(
              QUALIFICATION_TYPES.GRADUATION,
              candidateDetails.qualifications
            ),
            states
          )}

          {isPostGraduation ? (
            getQualificationView(
              "PG (if any)",
              "POSTGRADUATION",
              getQualificationDetails(
                QUALIFICATION_TYPES.POSTGRADUATION,
                candidateDetails.qualifications
              ),
              states
            )
          ) : (
              <View></View>
            )}
        </View>

        <View style={styles.tableWithMarginTop}>
          <View style={commonStyles.tableRow}>
            <View style={styles.fullWidthTableColumn}>
              <Text style={styles.tableHeader}>Professional Experience</Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.totalExperienceKey}>
              <Text style={commonStyles.tableCellWithoutAutoMargin}>
                Total Experience
              </Text>
            </View>
            <View style={styles.inYearsStyles}>
              <Text style={commonStyles.tableCellWithoutAutoMargin}>
                In Years
              </Text>
            </View>
            <View style={styles.totalExperienceValue}>
              <Text style={commonStyles.tableCellWithoutAutoMargin}>
                {getNoOfYearsMonthsDaysString(findTotalExperienceInDays(candidateDetails))}
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.lastExperienceTitle}>
              <Text style={commonStyles.tableCellWithoutAutoMargin}>
                Last 3 Full Time Employment Details (begin with latest)
              </Text>
            </View>
            <View style={styles.fromDate}>
              <Text style={commonStyles.tableCellWithoutAutoMargin}>
                From (Date)
              </Text>
            </View>
            <View style={styles.toDate}>
              <Text style={commonStyles.tableCellWithoutAutoMargin}>
                To (Date)
              </Text>
            </View>
            <View style={styles.designation}>
              <Text style={commonStyles.tableCellWithoutAutoMargin}>
                Designation
              </Text>
            </View>
            <View style={styles.grossPay}>
              <Text style={commonStyles.tableCellWithoutAutoMargin}>
                Gross Pay (P.A) in INR
              </Text>
            </View>
          </View>
          {candidateDetails.organizationDetails ? candidateDetails.organizationDetails.map(pastOrganization => (
            <View style={commonStyles.tableRow}>
              <View style={styles.lastExperienceTitle}>
                <Text style={commonStyles.tableCellWithoutAutoMargin}>
                  {pastOrganization.organizationName}
                </Text>
              </View>
              <View style={styles.fromDate}>
                <Text style={commonStyles.tableCellWithoutAutoMargin}>
                  {getFormattedDate(pastOrganization.joiningDate)}
                </Text>
              </View>
              <View style={styles.toDate}>
                <Text style={commonStyles.tableCellWithoutAutoMargin}>
                  {getFormattedDate(pastOrganization.lastWorkingDate)}
                </Text>
              </View>
              <View style={styles.designation}>
                <Text style={commonStyles.tableCellWithoutAutoMargin}>
                  {pastOrganization.jobTitle}
                </Text>
              </View>
              <View style={styles.grossPay}>
                <Text style={commonStyles.tableCellWithoutAutoMargin}>
                  {"  "}
                  ----------{" "}
                </Text>
              </View>
            </View>
          )) : null }
        </View>

        <Image break style={commonStyles.cdkLogo} src={CDK_Logo} />

        <View style={styles.tableWithMarginTop}>
          <View style={commonStyles.tableRow}>
            <View style={styles.fullWidthTableColumn}>
              <Text style={styles.tableHeaderWithoutAutoMargin}>
                Declaration by Applicant
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.fullWidthTableColumn}>
              <Text style={styles.tableHeader}>
                I confirm that the information provided byme in this application
                is accurate and correct. I understand that in the event, any of
                the information provided by me is found to be wrong, it could
                result in termination of my employment without any notice.
              </Text>
            </View>
          </View>
          <View
            style={{ ...commonStyles.tableRow, ...commonStyles.displayFlex }}
          >
            <View style={{ ...commonStyles.tableCol, ...commonStyles.width50 }}>
              <Text style={commonStyles.tableCellWithoutAutoMargin}>
                <Text style={commonStyles.textRed}>Date: </Text>
                {getFormattedDate(candidateDetails.declarationDate)}
              </Text>
            </View>
            <View style={styles.declarationNameAndSignature}>
              <View style={commonStyles.tableRow}>
                <View style={styles.declarationSignature}>
                  <Text style={commonStyles.tableCellWithoutAutoMargin}>
                    <Text style={commonStyles.textRed}>Signature: </Text>
                    {fullName}
                  </Text>
                </View>
              </View>
              <View style={commonStyles.tableRow}>
                <View style={styles.declarationName}>
                  <Text style={commonStyles.tableCellWithoutAutoMargin}>
                    <Text style={commonStyles.textRed}>Name: </Text>
                    {fullName}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>
      </Page>
    );
  }
);

export default PersonalProfile;
